/*
* libraries
*/
import { Component, OnInit, Input, EventEmitter, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
// services
import { MasterService } from '../../services/master.service'
import { MessageService } from '../../services/message.service';
import { StoreService } from '../../services/store.service'
import { SecurityService } from 'src/app/services/security.service';
import { environment } from '../../../environments/environment';
import * as words from '../../services/language'
import { LANGUAGES } from "../../constants/general";
import { Language } from 'src/app/models/language.model';
import { Lot } from 'src/app/models/lot.model';
import { Subscription } from "rxjs";

declare var $: any;

@Component({
    selector: 'app-nav-bar',
    templateUrl: './nav-bar.component.html',
    styleUrls: ['./nav-bar.component.scss'],
    standalone: false
})
export class NavBarComponent implements OnInit {

    @Input() sideBar = true

    @Output() navMessage$: EventEmitter<object> = new EventEmitter()

    // save the language
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    protected image: Object;

    // define the languages
    protected languages: Language[] = LANGUAGES

    protected words = words

    protected languageSelect: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    protected user = { firstName: '', lastName: '', corporateId: 0, corpName: '' }

    protected lots: Lot[] = []

    protected lotSelected: number = 0

    protected theme: string | null = null

    // A handle to navbar event messages so we can unsubscribe later.
    private navbarWatcher: Subscription | null = null

    /** Get the current version of the UI that is running. */
    get version() {
        return environment.version || 'unknwn'
    }

    /*
    * life cicles
    */
    constructor(private master: MasterService, private ms: MessageService, private store: StoreService, private router: Router, protected security: SecurityService) {}

    ngOnInit() {
        this.determineTheme()

        this.navbarWatcher = this.ms.channelComponents$.subscribe(res => {
            switch (res['message']) {
                case 'updateInformation':
                    this.user.firstName = res['data']['firstName']
                    this.user.lastName = res['data']['lastName']
                    this.user['phone'] = res['data']['phone']
                    this.store.userAccount['user'] = { ...this.user }
                    break
                case 'updateLotList':
                    this.lots = res['value']
                    this.store.userAccount['user'].lots = this.lots
                    this.determineLot()
                    break
            }
        })

        if (!this.store || !this.store.userAccount || !this.store.userAccount['user']) {
            this.readAccount()
            return
        }

        this.user = this.store.userAccount['user']
        this.changeLanguage(localStorage.getItem('language') ? localStorage.getItem('language') : this.user['languagePreference'])

        this.lots = null
        if (this.user && this.user['lots']) {
            this.lots = this.user['lots']
        }

        this.determineLot()
    }

    ngOnDestroy() {
        if (this.navbarWatcher) {
            this.navbarWatcher.unsubscribe()
            this.navbarWatcher = null
        }
    }

    /*
    * functions
    */

    /*
    ------------------------
    todo: change Language
    @param language:string=language selected
    / ------------------------
    */
    protected changeLanguage = (language: string): void => {
        this.languageSelect = language
        const returnedValue = this.languages.find(lang => lang['value'] == language)
        this.image = returnedValue['image']
        localStorage.setItem('language', this.languageSelect)

        this.navMessage$.emit({ message: 'changeLanguage', value: this.languageSelect })
        this.ms.channelComponents$.emit({ message: 'changeLanguage', section: 'navbar', value: this.languageSelect })
    }

    /*
    ------------------------
    todo: log out
    / ------------------------
    */
    protected logout = (): void => {
        this.master.discard('session', res => {
            // No concern for API errors here. Still 
            // want to clear the user token and 
            // redirect to login.
            localStorage.clear()
            sessionStorage.clear()
            
            this.master.reset()
            this.store.userAccount = {}
            this.router.navigate(['/login']);
        })
    }

    /*
    ------------------------
    todo: get user data
    / ------------------------
    */
    protected readAccount = (): void => {
        this.master.get(`users/account?lotId=${this.lotSelected}`, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: "API no response" })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            // set data to store service
            this.store.userAccount = res.data

            let route = window.location.hash
            let page = route.substring(2)

            // send permissions for all components
            this.ms.channelComponents$.emit({message: 'setPermissions', section: 'navbar', value: page})
            this.navMessage$.emit({message: 'setPermissions', value: this.languageSelect})

            // set data to object userInformation
            this.user = { ...this.store.userAccount['user'] }
            this.ms.channelComponents$.emit({ message: 'getUserAccount', section: 'navbar', value: this.user })
            this.ms.channelComponents$.emit({ message: 'reloadLogo', section: 'navbar', value: res.data.corporateLogo })
            this.navMessage$.emit({ message: 'getUserAccount', data: this.user })

            this.changeLanguage(this.user['languagePreference'])

            this.lots = null
            if (this.user && this.user['lots']) {
                this.lots = this.user['lots']
            }

            this.determineLot()
        })
    }

    /*
    ------------------------
    todo: log out
    @param lot:current lot
    / ------------------------
    */
    protected changeLot = (lot: number): void => {
        // save lot in local storage
        localStorage.setItem('lot', lot.toString())
        this.store.lotSelected = lot.toString()

        // page in
        let route = window.location.hash
        let page = route.substring(2)

        // emit message
        this.navMessage$.emit({ message: 'changeLot', lot: lot, page: page })
        this.ms.channelComponents$.emit({ message: 'changeLot', section: 'navbar', value: { lot: lot, page: page }})

        const roles = this.store.userAccount['roles']
        if (!roles) {
            this.store.userAccount['role'] = []
            return
        }

        const active = roles.filter(((role) => role.lotId == lot))
        
        const types = []
        for (const role of active) {
            types.push(role.type)
        }

        this.store.userAccount['role'] = types
    }

    /**
     * Determine the current lot based on what is available.
     */
    private determineLot = (): void => {
        if (!this.lots || (this.lots.length <= 0)) {
            localStorage.removeItem('lot')
            this.store.lotSelected = null
            return
        }

        if (localStorage.getItem('lot') === null) {
            // ! in case not exist in localstorage
            this.lotSelected = this.lots[0].id
            localStorage.setItem('lot', this.lotSelected.toString())
            this.changeLot(this.lotSelected)
            return
        }

        // ! in case exist localstorage
        this.lotSelected = parseInt(localStorage.getItem('lot'))

        const exist = this.lots.some(lot => lot.id == this.lotSelected)
        if (!exist) {
            this.lotSelected = this.lots[0].id
        }

        this.changeLot(this.lotSelected)
    }

    /**
     * 
     * @param e 
     * @param path 
     */
    protected navigate = (e: Event) => {
        if (!e) {
            return
        }

        const selection = (e.target as HTMLInputElement).value
        if (!selection || (selection.length <= 0)) {
            return
        }

        const chosen = (document.querySelector(`datalist#system-pages > option[value = '${selection}']`) as HTMLOptionElement)
        if (!chosen) {
            return
        }

        const path = chosen.dataset.path
        if (!path || (path.length <= 0)) {
            return
        }

        this.router.navigate([path])
    }

    private determineTheme = () => {
        this.theme = document.body.dataset.theme
        if (this.theme) {
            return
        }
        
        const darkMode = window.matchMedia('(prefers-color-scheme: dark)')
        if (darkMode.matches) {
            this.theme = 'dark'
            document.body.dataset.theme = 'dark'
            $.App.activateDarkMode()
            return
        }

        this.theme = 'light'
        document.body.dataset.theme = 'light'
        $.App.deactivateDarkMode()
    }

    protected toggleTheme = () => {
        if (!this.theme || (this.theme === 'dark')) {
            this.theme = 'light'
            document.body.dataset.theme = 'light'
            $.App.deactivateDarkMode()
            return
        }

        this.theme = 'dark'
        document.body.dataset.theme = 'dark'
        $.App.activateDarkMode()
    }
}
